import { composeReducers } from 'common/util';
import lodash from 'lodash';
import {
    USERS_CONFIG_LOAD_USERS,
    USERS_CONFIG_LOAD_PERMISSION_PROPERTIES,
    USERS_CONFIG_CHANGE_SELECTION,
    USERS_CONFIG_CHANGE_SEARCH,
    USERS_CONFIG_TOGGLE_SETTING,
    USERS_CONFIG_CHANGE_PERMISSIONS_DELTA,
    USERS_CONFIG_GRANT_ACCESS,
    USERS_CONFIG_REVOKE_ACCESS,
    USERS_CONFIG_INITIALIZE_PERMISSIONS_EDITOR,
    USERS_CONFIG_UNLOAD_USERS,
} from 'binder/actions/usersConfiguration';

export function loadUsers(state, action) {
    if (!action || action.type !== USERS_CONFIG_LOAD_USERS) {
        return state;
    }

    const usersPayload = action.payload;
    const groups = [];
    const users = [];

    for (const user of usersPayload) {
        const userGroups = user.groups;
        const {binderPermissions} = user;
        user.groups = lodash.map(userGroups, 'id');
        user.binderPermissions = lodash.keyBy(binderPermissions, 'name');
        user.initialBinderPermissions = lodash.cloneDeep(user.binderPermissions);
        groups.splice(groups.length, 0, ...userGroups);
        users.push(user);
    }

    const usersSection = lodash.keyBy(lodash.uniqBy(users, 'sub'), 'sub');
    const groupsSection = lodash.keyBy(lodash.uniqBy(groups, 'id'), 'id');

    return {
        ...state,
        users: {
            ...state.users,
            ...usersSection
        },
        groups: {
            ...state.groups,
            ...groupsSection
        }
    };
}

export function loadPermissionProperties(state, action) {
    if (!action || action.type !== USERS_CONFIG_LOAD_PERMISSION_PROPERTIES) {
        return state;
    }

    const permissionsPayload = action.payload;
    const permissions = [];

    for (const property of permissionsPayload) {
        const {name, displayName, permissionPropertyType} = property;
        const defaultValue = lodash.find(property.allowedValues, av => av.isDefault);
        const allowedValues = lodash.map(property.allowedValues, av => av.allowedValue);
        permissions.push({
            name,
            displayName,
            permissionPropertyType,
            value: defaultValue && defaultValue.allowedValue,
            allowedValues: allowedValues
        });
    }

    const permissionPropertiesSection = lodash.keyBy(permissions, 'name');

    return {
        ...state,
        permissionProperties: permissionPropertiesSection
    };
}

export function changeSelection(state, action) {
    if (!action || action.type !== USERS_CONFIG_CHANGE_SELECTION) {
        return state;
    }
    const {method, sub} = action.payload;
    const userPermissionsEditor = {...state.userPermissionsEditor};
    userPermissionsEditor.selectedUsers = userPermissionsEditor.selectedUsers
        ? [...userPermissionsEditor.selectedUsers]
        : [];
    userPermissionsEditor.permissionsDelta = {};
    let userIndex;
    switch (method) {
        case 'replace':
            userPermissionsEditor.selectedUsers = [sub];
            break;
        case 'add':
            if (!userPermissionsEditor.selectedUsers.includes(sub)) {
                userPermissionsEditor.selectedUsers.push(sub);
            }
            break;
        case 'remove':
            userIndex = userPermissionsEditor.selectedUsers.indexOf(sub);
            if (userIndex !== -1) {
                userPermissionsEditor.selectedUsers = [
                    ...userPermissionsEditor.selectedUsers.slice(0, userIndex),
                    ...userPermissionsEditor.selectedUsers.slice(userIndex + 1)
                ];
            }
            break;
        case 'all':
            if (userPermissionsEditor.viewAllUsers === 'all') {
                userPermissionsEditor.selectedUsers = [...userPermissionsEditor.visibleUsers];
            } else if (userPermissionsEditor.viewAllUsers === 'in') {
                userPermissionsEditor.selectedUsers =
                    lodash.intersection(
                        [...userPermissionsEditor.visibleUsers],
                        lodash.map(
                            lodash.filter(state.users, u => lodash.some(u.binderPermissions,
                                bp => bp.name === 'hasBinderAccess' && bp.value === 'yes')),
                            userWithAccess => userWithAccess.sub)
                    );
            } else {
                userPermissionsEditor.selectedUsers =
                    lodash.intersection(
                        [...userPermissionsEditor.visibleUsers],
                        lodash.map(
                            lodash.filter(state.users, u => lodash.some(u.binderPermissions,
                                bp => bp.name === 'hasBinderAccess' && bp.value !== 'yes')),
                            userWithAccess => userWithAccess.sub)
                    );
            }
            break;
        case 'none':
            userPermissionsEditor.selectedUsers = [];
            break;
        default:
            throw new RangeError('changeSelection: method must be "replace", "add", "remove", "all", or "none".');
    }
    return {
        ...state,
        userPermissionsEditor
    };
}

function matchesSearchString(value, searchString) {
    if (typeof value !== 'string' || typeof searchString !== 'string') {
        return false;
    }
    const pattern = new RegExp(lodash.escapeRegExp(searchString), 'i');
    return Boolean(value.match(pattern));
}

export function changeSearch(state, action) {
    if (!action || action.type !== USERS_CONFIG_CHANGE_SEARCH) {
        return state;
    }

    const {searchString} = action.payload;

    const userPermissionsEditor = {...state.userPermissionsEditor};
    userPermissionsEditor.visibleUsers = userPermissionsEditor.visibleUsers
        ? [...userPermissionsEditor.visibleUsers]
        : [];
    userPermissionsEditor.searchString = searchString;

    if (lodash.isEmpty(lodash.trim(searchString))) {
        userPermissionsEditor.visibleUsers = lodash.map(state.users, u => u.sub);
    } else {
        const filteredUsers = lodash.filter(state.users, user => {
            if (matchesSearchString(user.displayName, searchString)) return true;
            if (matchesSearchString(user.firstName, searchString)) return true;
            if (matchesSearchString(user.lastName, searchString)) return true;
            if (matchesSearchString(user.emailAddress, searchString)) return true;
            const groups = lodash.filter(state.groups, g => user.groups.includes(g.id));
            return lodash.some(groups, g => {
                return matchesSearchString(g.displayName, searchString);
            });
        });
        userPermissionsEditor.visibleUsers = lodash.map(filteredUsers, u => u.sub);
    }

    return {
        ...state,
        userPermissionsEditor
    };
}

export function toggleUserPermissionsEditorSetting(state, action) {
    if (!action || action.type !== USERS_CONFIG_TOGGLE_SETTING) {
        return state;
    }

    const {setting, value} = action.payload;

    if (!(['viewAllUsers', 'viewSummaryMode', 'isSaving']).includes(setting)) {
        return state;
    }

    const userPermissionsEditor = {...state.userPermissionsEditor};
    userPermissionsEditor[setting] = typeof value === 'undefined' ? !userPermissionsEditor[setting] : value;

    return {
        ...state,
        userPermissionsEditor
    };
}

export function changePermissionsDelta(state, action) {
    if (!action || action.type !== USERS_CONFIG_CHANGE_PERMISSIONS_DELTA) {
        return state;
    }

    const {name, value} = action.payload;

    const userPermissionsEditor = {...state.userPermissionsEditor};
    userPermissionsEditor.permissionsDelta = {...userPermissionsEditor.permissionsDelta};
    userPermissionsEditor.permissionsDelta[name] = value;

    return {
        ...state,
        userPermissionsEditor
    };
}

export function grantAccess(state, action) {
    if (!action || action.type !== USERS_CONFIG_GRANT_ACCESS) {
        return state;
    }

    const permissionItems = action.payload;

    const {selectedUsers} = state.userPermissionsEditor;

    if (lodash.isEmpty(permissionItems)) {
        return state;
    }

    const userPermissionsEditor = {...state.userPermissionsEditor};
    const permissionsDelta = {...userPermissionsEditor.permissionsDelta};
    userPermissionsEditor.unsavedChanges = true;
    userPermissionsEditor.permissionsDelta = {};
    userPermissionsEditor.selectedUsers = [];
    userPermissionsEditor.updatedUsers = lodash.isEmpty(userPermissionsEditor.updatedUsers)
        ? []
        : [...userPermissionsEditor.updatedUsers];

    const users = lodash.pickBy(state.users, u => selectedUsers.includes(u.sub));

    const updatedUsers = lodash.mapValues(users, user => {
        const updatedUser = {...user};
        userPermissionsEditor.updatedUsers.push(user.sub);
        updatedUser.binderPermissions = lodash.cloneDeep(updatedUser.binderPermissions);
        if(!lodash.isEmpty(permissionsDelta)) {
            lodash.keys(updatedUser.binderPermissions).forEach(key => {
                updatedUser.binderPermissions[key].custom = false;
            });
            lodash.mapKeys(permissionsDelta, (deltaValue, deltaName) => {
                if (updatedUser.binderPermissions[deltaName].isRestricted) {
                    return; // update only if they are not restricted;
                }
                updatedUser.binderPermissions[deltaName].name = deltaName;
                updatedUser.binderPermissions[deltaName].value = deltaValue;
                updatedUser.binderPermissions[deltaName].custom = true;
            });
        }
        updatedUser.binderPermissions.hasBinderAccess.value = 'yes';
        return updatedUser;
    });

    return {
        ...state,
        userPermissionsEditor,
        users: {
            ...state.users,
            ...updatedUsers
        }
    };
}

export function revokeAccess(state, action) {
    if (!action || action.type !== USERS_CONFIG_REVOKE_ACCESS) {
        return state;
    }

    const {selectedUsers} = state.userPermissionsEditor;

    const userPermissionsEditor = {...state.userPermissionsEditor};
    userPermissionsEditor.unsavedChanges = true;
    userPermissionsEditor.permissionsDelta = {};
    userPermissionsEditor.selectedUsers = [];
    userPermissionsEditor.updatedUsers = lodash.isEmpty(userPermissionsEditor.updatedUsers)
        ? []
        : [...userPermissionsEditor.updatedUsers];

    const users = lodash.pickBy(state.users, u => selectedUsers.includes(u.sub));
    const updatedUsers = lodash.mapValues(users, user => {
        userPermissionsEditor.updatedUsers.push(user.sub);
        const updatedUser = {...user};
        updatedUser.binderPermissions = lodash.cloneDeep(updatedUser.initialBinderPermissions);
        updatedUser.binderPermissions.hasBinderAccess.value = 'no';
        return updatedUser;
    });

    return {
        ...state,
        userPermissionsEditor,
        users: {
            ...state.users,
            ...updatedUsers
        }
    };
}

export function initializeUserPermissionsEditor(state, action) {
    if (!action || action.type !== USERS_CONFIG_INITIALIZE_PERMISSIONS_EDITOR) {
        return state;
    }
    // this purposefully does not change viewAllUsers or viewSummaryMode
    // so the cancel changes button will not reset these values.
    return {
        ...state,
        userPermissionsEditor: {
            ...state.userPermissionsEditor,
            searchString: '',
            unsavedChanges: false,
            selectedUsers: [],
            visibleUsers: [],
            updatedUsers: [],
            permissionsDelta: {},
        }
    };
}

export function unloadUsers(state, action) {
    if (!action || action.type !== USERS_CONFIG_UNLOAD_USERS) {
        return state;
    }

    const nextState = lodash.cloneDeep(state);
    lodash.unset(nextState, 'users');

    return nextState;
}

export default composeReducers(
    loadUsers,
    loadPermissionProperties,
    changeSelection,
    changeSearch,
    toggleUserPermissionsEditorSetting,
    changePermissionsDelta,
    grantAccess,
    revokeAccess,
    initializeUserPermissionsEditor,
    unloadUsers,
);
